import type { ReactNode } from 'react';

interface IconButtonProps {
  label: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  /**
   * Set to make it a toggle rather than an action — it then reports its state
   * to a screen reader and wears it.
   */
  pressed?: boolean;
  /**
   * What "on" looks like. `lit` is for the one that lights the wall itself, so
   * the button reads as a lamp that is burning rather than a pressed key.
   */
  pressedTone?: 'muted' | 'lit';
  /** Spelled out under the icon-only button, since there is no room for words. */
  title?: string;
}

const BASE =
  'flex size-9 shrink-0 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-40';

const TONES = {
  muted: 'border-transparent bg-muted text-foreground',
  // Warm, filled and haloed: the wall is on.
  lit: 'border-transparent bg-amber-400 text-amber-950 shadow-[0_0_0_3px_rgba(251,191,36,0.25)]',
} as const;

/**
 * The round icon-only buttons in the row under the board — what is done *to* it
 * or *with* it, as opposed to what is painted on it, which is the floating
 * picker's job.
 */
export default function IconButton({
  label,
  onClick,
  children,
  disabled = false,
  pressed,
  pressedTone = 'muted',
  title,
}: IconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed}
      aria-label={label}
      title={title ?? label}
      className={`${BASE} ${
        pressed ? TONES[pressedTone] : 'border-border text-muted-foreground'
      }`}
    >
      {children}
    </button>
  );
}
